import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { type ApiContainerAction, type ApiService, isBooked } from '@Model';
import { FrameEditService } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdateRequest,
    type Awaitable,
    isOk,
    LinkedField,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useTranslate,
} from '@synqo/frontend-component-library';
import { summarizeMaterial } from '@utils/containerMaterials';
import { useCallback, useMemo } from 'react';
import styles from './ContainerActions.module.css';
import { type ContainerMaterial } from './useContainerMaterial';

interface ContainerMaterialCellProps {
    /** The rows the cell speaks for: one container, or every row of a collective line. */
    actions: readonly ApiContainerAction[];
    /** Path to the container actions of the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** Whether the completed order is being corrected, which opens the material of booked rows too. */
    correcting?: boolean;
    material: ContainerMaterial;
    requestUpdateAction: (updater: AsyncStateUpdateRequest<ApiContainerAction>, actionId: string) => Awaitable<boolean>;
    autoOpen?: boolean;
}

/**
 * The material of one line. Choosing writes it to every row of the line that is not booked yet, and while
 * the order is corrected to the booked rows too, whose standing times saving then books anew.
 */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ContainerMaterialCell({
    actions,
    baseApiObjectPath,
    canEdit,
    correcting = false,
    material,
    requestUpdateAction,
    autoOpen,
}: ContainerMaterialCellProps) {
    const i18n = useTranslate('');
    const hasPermission = useHasPermission();

    const { material: shared, isMixed } = useMemo(() => summarizeMaterial(actions), [actions]);
    const openActions = useMemo(
        () => actions.filter((action) => correcting || !isBooked(action.booking_status)),
        [actions, correcting],
    );
    const anchor = openActions[0] ?? actions[0];
    const otherIds = useMemo(() => openActions.slice(1).map((action) => action.id), [openActions]);

    const requestUpdateState = useCallback(
        (updater: AsyncStateUpdateRequest<ApiContainerAction>) => requestUpdateAction(updater, anchor.id),
        [requestUpdateAction, anchor.id],
    );

    const shown = useMemo(
        () => ({ ...anchor, content: shared ?? { ...anchor.content, foreign_key: undefined, display_string: '' } }),
        [anchor, shared],
    );
    const binder = useGuiBinder<ApiContainerAction>({
        data: shown,
        meta: undefined,
        canEdit: canEdit && openActions.length > 0,
        requestUpdateState,
    });
    const linkMaterial = useLinkedField<ApiContainerAction, ApiService>(
        `${baseApiObjectPath}/${anchor.id}`,
        'content',
        FrameEditService,
        hasPermission(Permission.StammdatenServiceCreate),
        requestUpdateState,
    );
    const { linkApi } = linkMaterial;
    const materialField = useLinkProperty(binder, {
        ...linkApi,
        link: async (materialId) => {
            const linked = await linkApi.link(materialId);
            if (isOk(linked) && otherIds.length > 0) {
                await material.chooseMaterial(otherIds, materialId);
            }

            return linked;
        },
        unlink: async () => {
            const unlinked = await linkApi.unlink();
            if (isOk(unlinked) && shared?.foreign_key && otherIds.length > 0) {
                await material.clearMaterial(otherIds, shared.foreign_key);
            }

            return unlinked;
        },
    });

    if ((!canEdit || openActions.length === 0) && (isMixed || !shared?.foreign_key)) {
        return (
            <span className={styles.readOnlyValue}>
                {isMixed ? i18n('Verschiedene') : shared?.display_string || '—'}
            </span>
        );
    }

    return (
        <LinkedField<ApiService>
            {...materialField}
            {...linkMaterial.events}
            label={undefined}
            placeholder={isMixed ? i18n('Verschiedene') : i18n('Material wählen …')}
            locked={materialField.locked || material.isChoosing}
            autoFocusOnRender={autoOpen}
        />
    );
}
